import { useEffect, useState } from "react";
import {
  adminGetScorecardConfig, adminUpdateScorecardConfig, formatApiErrorDetail,
} from "@/lib/api";
import { Save, RotateCcw, Info, Lock } from "lucide-react";
import { toast } from "sonner";

export default function AdminScorecard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setCfg(await adminGetScorecardConfig()); }
    catch { toast.error("Could not load the scorecard config."); }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) {
    return (
      <div data-testid="admin-scorecard" className="animate-pulse space-y-4">
        <div className="h-8 w-72 bg-[rgba(28,27,25,0.08)] rounded" />
        <div className="h-40 bg-[rgba(28,27,25,0.06)] rounded" />
        <div className="h-40 bg-[rgba(28,27,25,0.06)] rounded" />
      </div>
    );
  }

  // ---- immutable setters ----
  const setIntro = (k, v) => setCfg((c) => ({ ...c, intro: { ...c.intro, [k]: v } }));
  const setSegment = (i, v) =>
    setCfg((c) => ({ ...c, segments: c.segments.map((s, idx) => (idx === i ? { ...s, label: v } : s)) }));
  const setQuestion = (i, k, v) =>
    setCfg((c) => ({ ...c, questions: c.questions.map((q, idx) => (idx === i ? { ...q, [k]: v } : q)) }));
  const setOption = (qi, oi, v) =>
    setCfg((c) => ({
      ...c,
      questions: c.questions.map((q, idx) =>
        idx === qi ? { ...q, options: q.options.map((o, j) => (j === oi ? v : o)) } : q),
    }));
  const setBand = (i, k, v) =>
    setCfg((c) => ({ ...c, bands: c.bands.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        intro: cfg.intro,
        segments: cfg.segments.map((s) => ({ value: s.value, label: s.label })),
        questions: cfg.questions.map((q) => ({
          key: q.key, category: q.category, tag: q.tag,
          prompt: q.prompt, recommendation: q.recommendation, options: q.options,
        })),
        bands: cfg.bands.map((b) => ({
          key: b.key, label: b.label, min: Number(b.min), max: Number(b.max), read: b.read,
        })),
      };
      await adminUpdateScorecardConfig(payload);
      toast.success("Saved. The Scorecard updates immediately for visitors.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  const maxScore = cfg.questions.length * 3;

  return (
    <div data-testid="admin-scorecard" className="pb-24">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="cv-kicker">Readiness Scorecard</p>
          <h1 className="cv-display text-3xl mt-4">The diagnostic, fully editable.</h1>
          <p className="cv-body text-sm text-[#1F1B16]/65 mt-3 max-w-xl">
            Edit the intro, the ten questions and their answer options, and the score bands. Changes go live the moment you save.
          </p>
        </div>
        <button data-testid="scorecard-save" onClick={save} disabled={busy}
          className="cv-btn-primary inline-flex items-center gap-2 self-start whitespace-nowrap disabled:opacity-60">
          <Save size={15} /> {busy ? "Saving..." : "Save changes"}
        </button>
      </div>

      <Note>
        Question keys and score logic are fixed (the recommended next step routing lives in code). You can edit all copy, the four options per question, and the band ranges. Bands should cover 0 to {maxScore}.
      </Note>

      {/* Intro */}
      <Section title="Intro (segment step)">
        <Field label="Headline">
          <input data-testid="scorecard-intro-headline" className="cv-input"
            value={cfg.intro.headline} onChange={(e) => setIntro("headline", e.target.value)} />
        </Field>
        <Field label="Sub-headline">
          <textarea data-testid="scorecard-intro-subhead" rows={2} className="cv-input resize-none"
            value={cfg.intro.subhead} onChange={(e) => setIntro("subhead", e.target.value)} />
        </Field>
        <Field label="Result footnote (the 'ten of fifty' line)">
          <input data-testid="scorecard-intro-tenoffifty" className="cv-input"
            value={cfg.intro.ten_of_fifty} onChange={(e) => setIntro("ten_of_fifty", e.target.value)} />
        </Field>
      </Section>

      {/* Segments */}
      <Section title="Segments (Q0 routing, not scored)">
        <div className="grid gap-3">
          {cfg.segments.map((s, i) => (
            <div key={s.value} className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 cv-sans text-[0.62rem] uppercase tracking-[0.1em] text-[#9A938A] w-28 shrink-0">
                <Lock size={11} /> {s.value}
              </span>
              <input data-testid={`scorecard-segment-${s.value}`} className="cv-input flex-1"
                value={s.label} onChange={(e) => setSegment(i, e.target.value)} />
            </div>
          ))}
        </div>
      </Section>

      {/* Questions */}
      <Section title={`Questions (${cfg.questions.length}, scored 0 to 3 each)`}>
        <div className="space-y-5">
          {cfg.questions.map((q, qi) => (
            <div key={q.key} data-testid={`scorecard-q-${q.key}`}
              className="border border-[rgba(28,27,25,0.16)] rounded-[14px] p-5">
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-1 cv-sans text-[0.62rem] uppercase tracking-[0.1em] text-[#9A938A]">
                  <Lock size={11} /> {q.key}
                </span>
                <span className="cv-sans text-[0.62rem] text-[#9A938A]">Q{qi + 1}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <Field label="Category">
                  <input data-testid={`scorecard-q-${q.key}-category`} className="cv-input"
                    value={q.category} onChange={(e) => setQuestion(qi, "category", e.target.value)} />
                </Field>
                <Field label="Tag (Build / Fix / Buy / Signal)">
                  <input data-testid={`scorecard-q-${q.key}-tag`} className="cv-input"
                    value={q.tag} onChange={(e) => setQuestion(qi, "tag", e.target.value)} />
                </Field>
              </div>
              <Field label="Prompt">
                <textarea data-testid={`scorecard-q-${q.key}-prompt`} rows={2} className="cv-input resize-none"
                  value={q.prompt} onChange={(e) => setQuestion(qi, "prompt", e.target.value)} />
              </Field>
              <Field label="Recommendation (shown in the opportunity map)">
                <textarea data-testid={`scorecard-q-${q.key}-recommendation`} rows={2} className="cv-input resize-none"
                  value={q.recommendation} onChange={(e) => setQuestion(qi, "recommendation", e.target.value)} />
              </Field>
              <p className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60 mt-3">
                Options (lowest to highest, scored 0 / 1 / 2 / 3)
              </p>
              <div className="grid gap-2 mt-2">
                {q.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-3">
                    <span className="cv-sans text-xs font-semibold text-[#A6633C] w-4 shrink-0">{oi}</span>
                    <input data-testid={`scorecard-q-${q.key}-opt-${oi}`} className="cv-input flex-1"
                      value={o} onChange={(e) => setOption(qi, oi, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Bands */}
      <Section title={`Score bands (cover 0 to ${maxScore})`}>
        <div className="space-y-4">
          {cfg.bands.map((b, i) => (
            <div key={b.key} data-testid={`scorecard-band-${b.key}`}
              className="border border-[rgba(28,27,25,0.16)] rounded-[14px] p-5">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1 cv-sans text-[0.62rem] uppercase tracking-[0.1em] text-[#9A938A] w-20 shrink-0">
                  <Lock size={11} /> {b.key}
                </span>
                <div className="flex-1 min-w-[140px]">
                  <input data-testid={`scorecard-band-${b.key}-label`} className="cv-input"
                    placeholder="Label" value={b.label} onChange={(e) => setBand(i, "label", e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                  <input data-testid={`scorecard-band-${b.key}-min`} type="number" className="cv-input w-20"
                    value={b.min} onChange={(e) => setBand(i, "min", e.target.value)} />
                  <span className="cv-body text-sm text-[#9A938A]">to</span>
                  <input data-testid={`scorecard-band-${b.key}-max`} type="number" className="cv-input w-20"
                    value={b.max} onChange={(e) => setBand(i, "max", e.target.value)} />
                </div>
              </div>
              <Field label="Read (the result line for this band)">
                <textarea data-testid={`scorecard-band-${b.key}-read`} rows={2} className="cv-input resize-none"
                  value={b.read} onChange={(e) => setBand(i, "read", e.target.value)} />
              </Field>
            </div>
          ))}
        </div>
      </Section>

      <div className="mt-10 flex items-center gap-3">
        <button data-testid="scorecard-save-bottom" onClick={save} disabled={busy}
          className="cv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
          <Save size={15} /> {busy ? "Saving..." : "Save changes"}
        </button>
        <button onClick={load} className="cv-btn-secondary inline-flex items-center gap-2">
          <RotateCcw size={14} /> Discard, reload saved
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-9">
      <div className="cv-hairline-copper mb-4" style={{ maxWidth: "3rem" }} />
      <h2 className="cv-h2 text-lg mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Note({ children }) {
  return (
    <div className="mt-6 flex items-start gap-2.5 rounded-[12px] border border-[rgba(28,27,25,0.14)] bg-[rgba(176,113,76,0.06)] p-4">
      <Info size={16} className="text-[#A6633C] mt-0.5 shrink-0" />
      <p className="cv-body text-sm text-[#1F1B16]/75">{children}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-3">
      <span className="cv-sans uppercase tracking-[0.12em] text-[0.66rem] text-[#1F1B16]/60">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
